import type { QBankStructureLevelItemWithLevel } from '@exam-management/qbs-provider';
import { Collapse, Flex, type FlexProps, type Theme } from '@shared/components';
import { useDisclosure } from '@shared/hooks';
import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';

import { AccordionEndAdornment, CustomCheckbox } from '../../components';
import { useQBSLevelItemSelection } from '../../providers';
import type { AddQBankToQConfigFormValues } from '../add-qbank-to-qconfig-form';

import { ChapterLevelItem } from './chapter-item';
import { getDefaultNameByQBSLevelCategory, getUnitByQBSLevelCategory } from './utils';

function getBgColorByIndent(indent: number, theme: Theme) {
  switch (indent) {
    case 0:
      return theme.palette.primary.light;
    case 1:
      return theme.palette.primary.lighter;
    default:
      return undefined;
  }
}

interface LevelItemAccordionProps extends FlexProps {
  item: QBankStructureLevelItemWithLevel;
  indent?: number;
}

export function LevelItemAccordion(props: Readonly<LevelItemAccordionProps>) {
  const { item, indent = 0, sx, ...restProps } = props;
  const { data, updateCheckbox, getCheckboxState } = useQBSLevelItemSelection();
  const formContext = useFormContext<AddQBankToQConfigFormValues>();
  const [isOpen, handler] = useDisclosure(indent !== 0);

  const label =
    item.tag?.name ?? getDefaultNameByQBSLevelCategory(item.qBankStructureLevel.category);

  const children = data.get(item.id);

  const isIndeterminate = useMemo(() => {
    return getCheckboxState(item.id).indeterminate;
  }, [item, data, formContext]);

  const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateCheckbox(item.id, e.target.checked);
  };

  const childrenSelectedCount = useMemo(() => {
    return children?.filter((child) => formContext.watch(`levelItems.${child.id}`)).length;
  }, [formContext, children]);
  const childrenLength = children?.length ?? 0;
  const childrenUnit = getUnitByQBSLevelCategory(children?.[0]?.qBankStructureLevel.category);
  const endAdornmentLabel = `${childrenSelectedCount}/${childrenLength} ${childrenUnit}`;

  return (
    <Flex
      {...restProps}
      sx={{
        flexDirection: 'column',
        ...sx,
      }}
    >
      <CustomCheckbox
        name={`levelItems.${item.id}`}
        defaultValue={false}
        label={label}
        indeterminate={isIndeterminate}
        slotProps={{
          typography: {
            variant: indent === 0 ? 'body1Semibold' : 'body1',
          },
          wrapper: {
            sx: (theme) => ({
              bgcolor: isOpen && children?.length ? getBgColorByIndent(indent, theme) : undefined,
              pl: theme.spacing(4 * indent + 4),
            }),
          },
        }}
        endAdornment={
          children ? (
            <AccordionEndAdornment
              label={endAdornmentLabel}
              expanded={isOpen}
              onExpandToggle={handler.toggle}
            />
          ) : null
        }
        onChange={handleOnChange}
      />
      <Collapse in={isOpen}>
        <Flex
          sx={(theme) => ({
            flexDirection: 'column',
            borderTop: '1px solid',
            borderColor: theme.palette.divider,
          })}
        >
          {children?.map((child, childIndex) => {
            const isChapter = child.qBankStructureLevel.category === 'CHAPTER';

            if (isChapter) {
              return <ChapterLevelItem key={child.id} item={child} indent={indent + 1} />;
            }

            return (
              <LevelItemAccordion
                item={child}
                key={child.id}
                indent={indent + 1}
                sx={{
                  borderBottom: childIndex === children?.length - 1 ? 'none' : '1px solid',
                  borderBottomColor: (theme) => theme.palette.divider,
                }}
              />
            );
          })}
        </Flex>
      </Collapse>
    </Flex>
  );
}
